import { createRef, useEffect, useState } from 'react'
import { MdHowToReg, MdOpenInNew } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { usePermission } from '@/lib/hooks/use-permission'
import { usePresence } from '@/lib/hooks/use-presence'
import PresenceAssign from './presence-assign'
import PresenceHistory from './presence-history'
import PresenceList from './presence-list'

type PresenceTab = 'PANEL' | 'ASSIGN' | 'HISTORY'

/**
 * Acesso fixo ao controle de presença no navbar.
 *
 * O indicador pisca a cada chegada ou troca de controle feita por qualquer
 * operador da empresa e só apaga quando este usuário abre o modal — do mesmo
 * jeito que a bolinha de mensagem nova.
 */
export default function PresenceBadge() {
    const navigate = useNavigate()
    const { canRead } = usePermission()
    const allowed = canRead('bookings', 'presence')

    const [isOpen, setIsOpen] = useState(false)
    const [tab, setTab] = useState<PresenceTab>('PANEL')
    const modalRef = createRef<HTMLDialogElement>()

    const {
        entries,
        statuses,
        loading,
        unseenCount,
        reload,
        loadPanel,
        markSeen,
    } = usePresence({ autoLoad: allowed })

    useEffect(() => {
        if (isOpen) {
            modalRef.current?.showModal()
            markSeen()
        } else {
            modalRef.current?.close()
        }
    }, [isOpen, markSeen])

    if (!allowed) return null

    const handleOpen = () => {
        setTab('PANEL')
        setIsOpen(true)
        reload()
    }

    return (
        <>
            <button
                type="button"
                className={`btn gap-2 indicator ${
                    unseenCount > 0 ? 'btn-warning !text-white' : 'btn-ghost'
                }`}
                title="Controle de presença"
                onClick={handleOpen}
            >
                {/* Só o ícone: a barra estava lotada e o `title` já diz o que é ao
                    passar o mouse. O contador continua visível — é ele que importa. */}
                <MdHowToReg className="text-xl" />
                {unseenCount > 0 && (
                    <span className="indicator-item flex h-4 w-4 items-center justify-center">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-error opacity-75" />
                        <span className="badge badge-error badge-xs relative">
                            {unseenCount > 9 ? '9+' : unseenCount}
                        </span>
                    </span>
                )}
                {unseenCount === 0 && entries.length > 0 && (
                    <span className="badge badge-neutral badge-sm indicator-item">
                        {entries.length}
                    </span>
                )}
            </button>

            <dialog
                ref={modalRef}
                className="modal"
                onClose={() => setIsOpen(false)}
            >
                {/* O conteúdo só existe enquanto o modal está aberto. Deixá-lo
                    montado o tempo todo põe a lista de presença — com selects e
                    campos — no DOM de TODA página do sistema, o que polui busca
                    por acessibilidade e convida o autofill do navegador. */}
                {isOpen && (
                    <div className="modal-box max-w-3xl !rounded-2xl">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <h3 className="text-lg font-semibold">
                                    Controle de presença
                                </h3>
                            </div>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm gap-2 !rounded-lg"
                                onClick={() => {
                                    setIsOpen(false)
                                    navigate('/bookings/presence')
                                }}
                            >
                                <MdOpenInNew className="text-base" />
                                <span className="hidden sm:inline">
                                    Abrir tela
                                </span>
                            </button>
                        </div>

                        <div role="tablist" className="tabs tabs-bordered mt-4">
                            <button
                                type="button"
                                role="tab"
                                className={`tab ${tab === 'PANEL' ? 'tab-active' : ''}`}
                                onClick={() => setTab('PANEL')}
                            >
                                Painel ({entries.length})
                            </button>
                            <button
                                type="button"
                                role="tab"
                                className={`tab ${tab === 'ASSIGN' ? 'tab-active' : ''}`}
                                onClick={() => setTab('ASSIGN')}
                            >
                                Definir controle
                            </button>
                            <button
                                type="button"
                                role="tab"
                                className={`tab ${tab === 'HISTORY' ? 'tab-active' : ''}`}
                                onClick={() => setTab('HISTORY')}
                            >
                                Histórico
                            </button>
                        </div>

                        <div className="mt-4 max-h-[60vh] overflow-y-auto pr-1">
                            {tab === 'PANEL' && (
                                <PresenceList
                                    entries={entries}
                                    statuses={statuses}
                                    loading={loading}
                                    onChanged={loadPanel}
                                />
                            )}
                            {tab === 'ASSIGN' && (
                                <PresenceAssign
                                    statuses={statuses}
                                    onAssigned={loadPanel}
                                />
                            )}
                            {tab === 'HISTORY' && <PresenceHistory />}
                        </div>

                        <div className="modal-action">
                            <button
                                type="button"
                                className="btn !rounded-lg"
                                onClick={() => setIsOpen(false)}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                )}
                <form method="dialog" className="modal-backdrop">
                    <button type="submit">Fechar</button>
                </form>
            </dialog>
        </>
    )
}
